/**
 * 権限管理の管理画面 (ユーザー管理 / 権限グループ管理 / 権限付与) で共用する表示用のラベル (権限管理 SBI-8)。
 */
import type { AdminUser, PermissionFeature, PermissionGroupSummary } from '../api/types';

/** 機能カタログの区分 (`scope`) の表示名と並び順 (チェックボックスを区分ごとにまとめる) */
export const FEATURE_SCOPE_LABELS: readonly { scope: string; label: string }[] = [
  { scope: 'control_panel', label: 'ControlPanel' },
  { scope: 'admin', label: '権限管理 (管理者向け)' },
];

/** 機能を区分ごとにまとめる (区分内の順はカタログの定義順、未知の区分は末尾にそのまま出す)。 */
export function groupFeaturesByScope(
  features: readonly PermissionFeature[],
): { scope: string; label: string; features: PermissionFeature[] }[] {
  const known = FEATURE_SCOPE_LABELS.map(({ scope, label }) => ({
    scope,
    label,
    features: features.filter((f) => f.scope === scope),
  }));
  const knownScopes = new Set(FEATURE_SCOPE_LABELS.map((s) => s.scope));
  const unknownScopes = [
    ...new Set(features.filter((f) => !knownScopes.has(f.scope)).map((f) => f.scope)),
  ];
  const unknown = unknownScopes.map((scope) => ({
    scope,
    label: scope,
    features: features.filter((f) => f.scope === scope),
  }));
  return [...known, ...unknown].filter((group) => group.features.length > 0);
}

/** 機能キーを表示名に変える (カタログに無いキーはキーをそのまま出す)。 */
export function featureLabels(
  keys: readonly string[],
  catalog: readonly PermissionFeature[],
): string[] {
  const byKey = new Map(catalog.map((f) => [f.key, f.label]));
  return keys.map((key) => byKey.get(key) ?? key);
}

/** ユーザーの状態の表示名。無効化済みを最優先する。 */
export function userStatusLabel(user: Pick<AdminUser, 'enabled' | 'status'>): string {
  if (!user.enabled) return '無効';
  switch (user.status) {
    case 'CONFIRMED':
      return '利用中';
    case 'FORCE_CHANGE_PASSWORD':
      return '招待中 (初回ログイン前)';
    case 'UNCONFIRMED':
      return 'メール確認待ち';
    case null:
    case undefined:
      return '不明';
    default:
      return user.status;
  }
}

/** 所属グループの表示 (セル用)。所属ゼロは「なし (権限なし)」。 */
export function groupNamesLabel(groups: readonly PermissionGroupSummary[]): string {
  return groups.length === 0 ? 'なし (権限なし)' : groups.map((g) => g.name).join('、');
}

/** ユーザーの表示名 (メールが無ければ sub)。 */
export function userDisplayName(user: Pick<AdminUser, 'email' | 'user_sub'>): string {
  return user.email ?? user.user_sub;
}
